/* eslint-disable class-methods-use-this */
import { Requests, splitArrayByParts } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { Client } from '../../domain/client/Client';
import { ClientDTO } from '../../domain/client/ClientDTO';
import { ClientFlat } from '../../domain/client/ClientFlat';
import { ClientFlatDTO } from '../../domain/client/ClientFlatDTO';
import { Project } from '../../domain/project/Project';
import { ClientMapper } from '../../mappers/client/ClientMapper';
import { SessionRepository } from '../session/SessionRepository';
import { ClientRepository } from './ClientRepository';

export class ClientRepositoryApi implements ClientRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async fetchClient({ clientId, project }: { clientId: string; project: Project }): Promise<Client> {
    const response = await this.requests.get({
      uri: `/v2/client/${clientId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as ClientDTO;
    return ClientMapper.toDomain(data, project.custom_fields?.client ?? []);
  }

  async fetchSomeClients({
    clientIds,
    project,
  }: {
    clientIds: string[];
    project: Project;
  }): Promise<Array<Client | null>> {
    const clientsIdsChunks = splitArrayByParts({ inputArray: clientIds, itemsPerChunk: 500 }) as string[];

    const requests = clientsIdsChunks.map((clientIdsChunk) => {
      return {
        method: this.requests.post.bind(this.requests),
        params: {
          uri: `/v2/retrieveClientsById`,
          data: {
            client_ids: clientIdsChunk,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const clients = response.flat(1) as ClientDTO[];

    return clients.map((client: ClientDTO | null) => {
      return client !== null ? ClientMapper.toDomain(client, project.custom_fields?.client ?? []) : null;
    });
  }

  async save(client: Client, project: Project): Promise<Client> {
    const response = await this.requests.put({
      uri: `/v2/client/${client.id}`,
      data: ClientMapper.toRepository(client),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return ClientMapper.toDomain(response.data, project.custom_fields?.client ?? []);
  }

  async createFromStops({ stopIds, project }: { stopIds: Array<string>; project: Project }): Promise<Array<Client>> {
    const response = await this.requests.post({
      uri: `/v2/clients/from_stops?project_id=${project.id}`,
      data: { stop_ids: stopIds },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newClients = response.data as Array<ClientDTO>;
    return newClients.map((newClient) => ClientMapper.toDomain(newClient, project.custom_fields?.client ?? []));
  }

  async createClients({ clients, project }: { clients: Array<Client>; project: Project }): Promise<Array<Client>> {
    const response = await this.requests.post({
      uri: `/v2/clients?project_id=${project.id}`,
      data: clients.map(ClientMapper.toRepository),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newClients = response.data as Array<ClientDTO>;
    return newClients.map((newClient) => ClientMapper.toDomain(newClient, project.custom_fields?.client ?? []));
  }

  async updateConstraints(clientIds: string[], constraints: Record<string, any>): Promise<void> {
    await this.requests.put({
      uri: `/v2/clients/constraints`,
      data: {
        client_ids: clientIds,
        constraints,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async searchClients({
    project,
    offset,
    limit,
    searchText,
    sortBy,
    sortDirection,
    deleted,
  }: {
    project: Project;
    offset?: number;
    limit?: number;
    searchText?: string;
    sortBy?: string;
    sortDirection?: string;
    deleted?: boolean;
  }): Promise<PaginatedResult<Client>> {
    const response = await this.requests.get({
      uri: `/v2/clients/search`,
      params: {
        project_id: project.id,
        ...(searchText ? { text: searchText } : {}),
        ...(sortBy ? { sort: sortBy } : {}),
        ...(sortBy && sortDirection ? { sort: `${sortBy}:${sortDirection}` } : {}),
        ...(offset ? { offset } : {}),
        ...(limit ? { limit } : {}),
        ...(deleted !== undefined ? { deleted } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as PaginatedResult<ClientDTO>;

    return {
      ...data,
      docs: data.docs.map((newClient) => ClientMapper.toDomain(newClient, project.custom_fields?.client ?? [])),
    };
  }

  async getFlatClients(projectId: string): Promise<ClientFlat[]> {
    const response = await this.requests.get({
      uri: `/v2/clients/flat?project_id=${projectId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const data = response.data as ClientFlatDTO[];

    return data.map(ClientMapper.toFlatDomain);
  }

  async deleteClients(clientIds: string[], project: Project): Promise<Client[]> {
    const requests = clientIds.map((clientId) => {
      return {
        method: this.requests.delete.bind(this.requests),
        params: {
          uri: `/v2/client/${clientId}`,
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const deletedClients = response.flat(1) as ClientDTO[];

    return deletedClients
      .filter((driver) => driver !== null)
      .map((driver: ClientDTO) => ClientMapper.toDomain(driver, project.custom_fields?.client ?? []));
  }
}
